import React from "react"

import { LeadKpiTile, type LeadKpiTileData } from "./kpi/LeadKpiTile.tsx"
import {
  type LeadKpiCompare,
  describeLeadKpiCompare
} from "./kpi/lead-kpi-compare.ts"
import {
  isLeadKpiActive,
  leadKpiPresentation
} from "./kpi/lead-kpi-presentation.ts"
import type { LeadsFilters } from "../../redux/leads.types.ts"

interface LeadsKpiRowProps {
  tiles?: LeadKpiTileData[] | null
  loading?: boolean
  filters?: LeadsFilters | null
}

/** Placeholders while the first request is in flight, so the row holds its height. */
const SKELETON_TILES: LeadKpiTileData[] = [
  {
    id: "s-1",
    title: "Active Leads",
    value: "—",
    icon: "people",
    tint: "#3B82F6"
  },
  { id: "s-2", title: "Hot Leads", value: "—", icon: "flame", tint: "#EF4444" },
  {
    id: "s-3",
    title: "Follow Ups Today",
    value: "—",
    icon: "clock",
    tint: "#F59E0B"
  }
]

/**
 * The design's KPI strip. It renders whatever the payload carries rather than a
 * fixed list, so the tiles still awaiting a definition from the client (PNS,
 * Long Parked, Exchange Opps) appear the moment the backend sends them — see
 * docs/specs/2026-08-13-leads-stats-contract.md.
 */
export function LeadsKpiRow({ tiles, loading, filters }: LeadsKpiRowProps) {
  const shown = loading && !tiles?.length ? SKELETON_TILES : (tiles ?? [])

  if (!shown.length) return null

  return (
    // auto-fit, not a fixed column count: the row carries three tiles today and
    // seven once the endpoint lands, and a 7-column grid holding 3 tiles starves
    // each one until its title ellipses.
    <div
      data-testid="leads-kpi-row"
      className={"grid gap-3"}
      style={{ gridTemplateColumns: "repeat(auto-fit, minmax(180px, 232px))" }}
    >
      {shown.map((tile) => {
        // The endpoint sends data, not appearance. Glyph, colour and drill
        // target are resolved here from the tile's id, so a payload that
        // carries none of them still renders as designed.
        const { icon, tint, drillTo, higherIsBetter } = leadKpiPresentation(
          tile.id
        )
        // The endpoint reports the movement; how it reads — arrow, wording,
        // colour — is decided here alongside the rest of the presentation.
        const compare = (tile as { compare?: LeadKpiCompare | null }).compare
        return (
          <LeadKpiTile
            key={tile.id}
            selected={isLeadKpiActive(tile.id, filters)}
            tile={{
              ...tile,
              icon,
              tint,
              drillTo: tile.drillTo ?? drillTo,
              delta:
                tile.delta ??
                describeLeadKpiCompare(compare, { higherIsBetter })
            }}
            loading={loading}
          />
        )
      })}
    </div>
  )
}
